'use client';

/**
 * "Has this element been on screen yet" -- a one-way latch for deferring work until a card is actually looked at.
 *
 * Built for the assistant thread, where every function card and property card fetched on MOUNT. Opening a conversation
 * therefore issued one request per card in the whole history, including cards forty messages up that the user never
 * scrolls to. The requests are per-card by design; what was missing is that nothing gated them on visibility.
 *
 * ONE-WAY on purpose. A card that has been seen keeps its data: re-latching on every scroll-back would refetch content
 * the component already holds, which is the opposite of the fix.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
// Brand-agnostic CSS variable refs (DS-BRAND-IMPORT-001): shared components consume the css-refs token objects, never
// direct design-system brand tokens.
import { spacing } from '@federa/components/css-refs.spacing';
import { CSS_VAR_NAME_REGEX } from '@federa/const/css-var-tokens';
import { positiveFiniteOrNull } from '@federa/const/number-guards';
import type { MutableRefObject } from 'react';

/** Authoring-time mirror of the brand `--space-contextual-viewport-preload` value, used wherever the custom property
 *  cannot be read: a non-DOM environment, or a host that has not loaded the brand CSS. */
const FALLBACK_VIEWPORT_PRELOAD_PX = 200;

let cachedRootMargin: string | null = null;

/**
 * `rootMargin` starts the fetch slightly BEFORE the card scrolls into view, so a normal scroll lands on content rather
 * than on a placeholder that resolves a moment later. Wide enough to hide the request, narrow enough that a long
 * conversation still does not fetch its whole history. The distance is the design system's own
 * `spacing.contextual.viewportPreload`, whose token JSDoc names this exact consumer.
 *
 * IntersectionObserver parses `rootMargin` ITSELF and rejects anything that is not px/%, so a `var()` is not merely
 * unresolved there -- it throws at construction. The css-refs reference is therefore resolved to its RAW value off the
 * root element at runtime, the same treatment `useIsMobile` gives `breakpoints.md` for `matchMedia`, and stays
 * brand-agnostic because the number comes from whichever brand CSS the host loaded. Resolved ONCE: the value cannot
 * change without a brand-CSS swap, and a per-card `getComputedStyle` would bill a style recalculation against exactly
 * the long-conversation path this hook exists to make cheap.
 */
function viewportPreloadRootMargin(): string {
	if (cachedRootMargin !== null) return cachedRootMargin;
	const varName = CSS_VAR_NAME_REGEX.exec(spacing.contextual.viewportPreload)?.[1];
	const raw = varName && typeof document !== 'undefined' ? getComputedStyle(document.documentElement).getPropertyValue(varName) : '';
	const parsed = Number.parseFloat(raw);
	const px = positiveFiniteOrNull(parsed) ?? FALLBACK_VIEWPORT_PRELOAD_PX;
	cachedRootMargin = `${px}px`;
	return cachedRootMargin;
}

/** Module-local: `createInViewLatch` is the observable seam, and its degraded arm is what a test asserts. */
function isIntersectionObserverAvailable(): boolean {
	return typeof IntersectionObserver !== 'undefined';
}

/**
 * Observe `element` and call `onVisible` the FIRST time it intersects, then stop. Returns a teardown.
 *
 * Degrades toward VISIBLE: with no IntersectionObserver (an older browser, a non-DOM test env) it fires immediately, so
 * the caller fetches eagerly rather than rendering a spinner that never resolves. Eager is the old behaviour; stuck is
 * a new bug, and a gate on visibility must never be able to hide content permanently.
 */
export function createInViewLatch(element: Element, onVisible: () => void): () => void {
	if (!isIntersectionObserverAvailable()) {
		onVisible();
		return () => undefined;
	}
	let fired = false;
	const observer = new IntersectionObserver(
		(entries) => {
			if (fired || !entries.some((entry) => entry.isIntersecting)) return;
			fired = true;
			observer.disconnect();
			onVisible();
		},
		{ rootMargin: viewportPreloadRootMargin() },
	);
	observer.observe(element);
	return () => {
		observer.disconnect();
	};
}

/** Run whatever latch teardown the slot is holding and empty it, so the slot never keeps a teardown that has already
 *  fired. The re-point path and the unmount path make the same decision, which is why both spelled it out. */
function releaseLatchSlot(slot: MutableRefObject<(() => void) | null>): void {
	slot.current?.();
	slot.current = null;
}

/** Attach `ref` to the element to watch; `inView` flips true once it has been seen and never flips back. */
export function useInViewOnce(): { ref: (node: Element | null) => void; inView: boolean } {
	const [inView, setInView] = useState(false);
	const teardownRef = useRef<(() => void) | null>(null);

	// A CALLBACK ref, not an object ref: the observed element is the card's own wrapper, which renders on the first pass
	// and would need a second effect pass to be picked up by a `useRef` + `useEffect` pair.
	const ref = useCallback((node: Element | null) => {
		releaseLatchSlot(teardownRef);
		if (!node) return;
		teardownRef.current = createInViewLatch(node, () => setInView(true));
	}, []);

	useEffect(() => {
		return () => {
			releaseLatchSlot(teardownRef);
		};
	}, []);

	return { ref, inView };
}
